/* eslint-disable react/no-array-index-key */
// dependencies
import { arrayMove } from "@dnd-kit/sortable";
import { List, Map, fromJS } from "immutable";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { faCheck, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { InputWrapper } from "@performyard/py-components";
// components
import { Avatar } from "@performyard/py-components";

import DraggableEmployee from "../../components/DragAndDrop/DraggableEmployee";
import EmployeePicker from "../../components/EmployeePicker/compatibility";

// hooks
import useEmployeeList from "./hooks/useEmployeeList";

// utils
import isPlainObject from "../../utils/isPlainObject";

// config
import prependOptions from "../../constants/EmployeePickerPrependReview";
import BeforeUnload from "../BeforeUnload";
import DragAndDrop from "../DragAndDrop";
import DatePicker from "../Inputs/DatePicker";
import { SingleSelectForm } from "../Inputs/SelectForm";
import TextInput from "../Inputs/TextInput";
import Toggle from "../Inputs/ToggleSwitches/ThumbSwitch";
// styles
import css from "./ReviewFormCard.module.scss";

/**
 * Creates a card for a review form.
 *
 * This was first used on the creat review cycle page. Since that page had a
 * criterion that it showed an error if any of the due dates were before the
 * start date of the cycle, I had to add to props to this component that I'm
 * not proud of:
 *
 * { minimumDueDate, invalidMinimumDueDateMessage }
 *
 * This will check if the author or signer due date is less than that
 * 'minimumDueDate' prop. If so, it will display the cusom error message set in
 * the 'invalidMinimumDueDateMessage' prop.
 * @param {object} props
 */
const BasicReviewFormCard = (props) => {
	const {
		name,
		authors,
		signers,
		redacted,
		className = "",
		isEditing = false,
		isDeleted = false,
		lockedFields = List(),
		minimumDueDate = null,
		authorsDueDate,
		signersDueDate,
		visibleToSubject,
		invalidMinimumDueDateMessage = null,
		editableForm = undefined,
		dateType = "static",
		authorRequired = false,
		isNewForm,
		onEdit = () => {},
		onDelete = () => {},
		onCancel = () => {},
		onSave = () => {},
	} = props;

	const formIsSelectable = editableForm !== undefined;

	/**
	 * Local states are used to manage the editing state of the form values.
	 */
	const [localRedacted, setRedacted] = useState(redacted);
	const [localAuthorsDueDate, setAuthorsDueDate] = useState(authorsDueDate);
	const [localSignersDueDate, setSignersDueDate] = useState(signersDueDate);
	const [localVisibleToSubject, setVisibleToSubject] = useState(visibleToSubject);
	const [localAuthors, authorPickerValue, handleAuthorsChange, removeAuthor, setAuthors, resetAuthors] =
		useEmployeeList(authors);
	const [localSigners, signerPickerValue, handleSignersChange, removeSigner, setSigners, resetSigners] =
		useEmployeeList(signers);
	const [localEditableForm, setLocalEditableForm] = useState(editableForm === null ? "" : editableForm);

	/**
	 * When any of these props change, reset the corresponding local state
	 * value.
	 */
	useEffect(() => {
		setRedacted(redacted);
	}, [redacted]);

	useEffect(() => {
		setAuthorsDueDate(authorsDueDate);
	}, [authorsDueDate]);

	useEffect(() => {
		setSignersDueDate(signersDueDate);
	}, [signersDueDate]);

	useEffect(() => {
		setVisibleToSubject(visibleToSubject);
	}, [visibleToSubject]);

	useEffect(() => {
		resetAuthors();
	}, [authors]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		resetSigners();
	}, [signers]); // eslint-disable-line react-hooks/exhaustive-deps

	// if signers are added, set the default signer due date
	// to be one day after author due date
	useEffect(() => {
		if (localSigners.size === 1 && moment.isMoment(localAuthorsDueDate) && moment.isMoment(localSignersDueDate)) {
			if (localSignersDueDate.isBefore(localAuthorsDueDate)) {
				setSignersDueDate(localAuthorsDueDate.add(1, "days"));
			}
		}
	}, [localSigners.size]);

	/**
	 * The author due date must be the same or before a signer due date
	 * if signers exist. If it
	 * isn't, we don't want the user to be able to save the form.
	 */
	let validDueDates = true;

	if (dateType === "static") {
		if (localSigners.size > 0 && moment.isMoment(localAuthorsDueDate) && moment.isMoment(localSignersDueDate)) {
			validDueDates = localAuthorsDueDate.isSameOrBefore(localSignersDueDate);
		}
	}

	/**
	 * If the component has a minimum due date, we need to make sure that both
	 * the author due date and the signer due date are the same or greater than
	 * the minimum due date.
	 */
	let isAboveLowerDateBoundary = true;

	if (minimumDueDate && (moment.isMoment(localAuthorsDueDate) || moment.isMoment(localSignersDueDate))) {
		if (!localAuthorsDueDate || !localAuthorsDueDate.isSameOrAfter(minimumDueDate)) {
			isAboveLowerDateBoundary = false;
		}
		if (!localSignersDueDate || (localSigners.size > 0 && !localSignersDueDate.isSameOrAfter(minimumDueDate))) {
			isAboveLowerDateBoundary = false;
		}
	} else if (!localAuthorsDueDate && !localSignersDueDate) {
		isAboveLowerDateBoundary = false;
	}

	const formSelected = !(formIsSelectable && localEditableForm === "");

	const authorRequiredAndSelected = !(authorRequired && localAuthors.size === 0);

	/**
	 * True if we have at least 1 error.
	 */
	const hasError =
		[validDueDates, isAboveLowerDateBoundary, formSelected, authorRequiredAndSelected].indexOf(false) >= 0;

	// check if there are any differences between local and saved values
	const checkFormChange = () => {
		const localForm = localEditableForm ? localEditableForm.get("id", null) : null;
		const savedForm = editableForm ? editableForm.get("id", null) : null;

		const allFormAttributes = {
			formChanged: localForm !== savedForm,
			authorsChanged: JSON.stringify(authors) !== JSON.stringify(localAuthors),
			signersChanged: JSON.stringify(signers) !== JSON.stringify(localSigners),
			redactedChanged: redacted !== localRedacted,
			authorsDueDateChanged: authorsDueDate !== localAuthorsDueDate,
			signersDueDateChanged: signersDueDate !== localSignersDueDate,
			visibleToSubjectChanged: visibleToSubject !== localVisibleToSubject,
		};

		return Object.values(allFormAttributes).some((value) => value === true);
	};

	const handleSave = () => {
		if (!validDueDates) {
			return;
		}

		let authorDueDate = localAuthorsDueDate;
		let signerDueDate = localSigners.size > 0 ? localSignersDueDate : localAuthorsDueDate;

		// default dates to 0 if values deleted
		if (dateType === "relative") {
			if (localAuthorsDueDate === "") {
				authorDueDate = 0;
				setAuthorsDueDate(0);
			}
			if (localSignersDueDate === "") {
				signerDueDate = 0;
				setSignersDueDate(0);
			}
		}

		onSave({
			redacted: localRedacted,
			authors: localAuthors.toJS(),
			signers: localSigners.toJS(),
			authorsDueDate: authorDueDate,
			signersDueDate: signerDueDate,
			visibleToSubject: localVisibleToSubject,
			form: formIsSelectable && localEditableForm !== null ? localEditableForm.toJS() : null,
		});
	};

	const handleCancel = () => {
		// reset defaults
		resetAuthors();
		resetSigners();
		setRedacted(redacted);
		setAuthorsDueDate(authorsDueDate);
		setSignersDueDate(signersDueDate);
		setVisibleToSubject(visibleToSubject);
		setLocalEditableForm(editableForm);

		// cancel hook
		onCancel();
	};

	const handleReviewFormChange = (value) => {
		// Convert from new format {_id, name} to legacy immutable format {id, name}
		if (value) {
			const legacyValue = fromJS({
				id: value._id,
				name: value.name,
			});
			setLocalEditableForm(legacyValue);
		} else {
			setLocalEditableForm(value);
		}
	};

	const onDragEndAuthors = (event) => {
		const { active, over } = event;

		if (active.id !== over.id) {
			const authors = localAuthors.toJS();
			const activeIndex = authors.findIndex((item, ii) => `${item.id}-${ii}` === active.id);
			const overIndex = authors.findIndex((item, ii) => `${item.id}-${ii}` === over.id);
			const newAuthors = arrayMove(authors, activeIndex, overIndex);

			// update store
			setAuthors(fromJS(newAuthors));
		}
	};

	const onDragEndSigners = (event) => {
		const { active, over } = event;
		if (active.id !== over.id) {
			const signers = localSigners.toJS();
			const activeIndex = signers.findIndex((item, ii) => `${item.id}-${ii}` === active.id);
			const overIndex = signers.findIndex((item, ii) => `${item.id}-${ii}` === over.id);
			const newSigners = arrayMove(signers, activeIndex, overIndex);

			// update store
			setSigners(fromJS(newSigners));
		}
	};

	const formatDraggableItems = (employee, removeItem, testID, index) => {
		return (
			<DraggableEmployee
				key={employee.id}
				employeeId={employee.id}
				employeeName={employee.name}
				onRemove={() => removeItem(index)}
				data-testid={testID}
				version="old"
			/>
		);
	};

	/** When in editing state, it should use the state data. If not in an eding
	 * state, it should use the props.
	 */
	const displayAuthors = isEditing ? localAuthors : authors;
	const displaySigners = isEditing ? localSigners : signers;

	/**
	 * The redactedness of an a form can only be edited by an admin.
	 * A group admin cannot toggle the redactedness.
	 * For a group admin and a redacted form -
	 *      always show the green checkmark (even if they are editing)
	 * For a group admin and a not redacted form -
	 *      do not show the field on the card at all.
	 */
	let redactedSection = null;

	if (lockedFields.includes("redactedness")) {
		redactedSection = redacted ? (
			<div data-testid="redactValue" className={`${css.attributeValue} ${redacted ? css.isGreen : css.isRed}`}>
				<FontAwesomeIcon data-testid="fas-check" icon={faCheck} />
			</div>
		) : null;
	} else {
		redactedSection = isEditing ? (
			<div className={css.toggleWrapper}>
				<Toggle onChange={setRedacted} value={localRedacted} width={40} />
			</div>
		) : (
			<div data-testid="redactValue" className={`${css.attributeValue} ${redacted ? css.isGreen : css.isRed}`}>
				{redacted ? (
					<FontAwesomeIcon data-testid="fas-check" icon={faCheck} />
				) : (
					<FontAwesomeIcon data-testid="fas-times" icon={faTimes} />
				)}
			</div>
		);
	}
	return (
		<div
			data-testid="basicReviewFormCard"
			className={`${css.templateForm} ${isEditing ? css.templateIsEditing : ""} ${className}`}
		>
			<div className={css.content}>
				<div>
					{formIsSelectable && isDeleted && <div className={css.isDeletedLabel}>Form has been deleted</div>}

					{formIsSelectable && (
						<div data-testid="select-review-form" className={css.attributeContainer}>
							<div className={`${css.attributeLabel} ${css.gridCell}`}>Select a form:</div>
							{isEditing ? (
								<InputWrapper id="addForm">
									<SingleSelectForm
										value={
											localEditableForm && typeof localEditableForm.get === "function"
												? {
														_id: localEditableForm.get("id"),
														name: localEditableForm.get("name"),
													}
												: undefined
										}
										onChange={handleReviewFormChange}
										placeholder="Enter form name..."
									/>
								</InputWrapper>
							) : (
								name
							)}
						</div>
					)}

					{!formIsSelectable && (
						<div data-testid="formName" className={css.formNameContainer}>
							<div className={css.formName}>{name}</div>
							{isDeleted ? <div className={css.isDeletedLabel}>Form has been deleted</div> : null}
						</div>
					)}

					<div data-testid="authorsContainer" className={`${css.attributeContainer} ${css.multipleValues}`}>
						<div className={`${css.attributeLabel} ${css.gridCell} ${isEditing ? css.editView : ""}`}>
							Who authors this form:
						</div>
						<div className={`${css.employeesList} ${css.gridCell} ${isEditing ? css.isEditing : ""}`}>
							{isEditing ? (
								<DragAndDrop
									itemIds={displayAuthors.map((author, ii) => `${author.get("id")}-${ii}`).toJS()}
									draggableItems={displayAuthors.toJS().map((author, ii) => ({
										...author,
										drag_id: `${author.id}-${ii}`,
									}))}
									keyValue="drag_id"
									handleOnDragEnd={onDragEndAuthors}
									formatDraggableItems={(author, index) =>
										formatDraggableItems(author, removeAuthor, "draggableAuthorItem", index)
									}
								/>
							) : (
								displayAuthors.map((employee, ii) => (
									<div
										data-testid="authorItem"
										key={`${employee.get("id")}-${ii}`}
										className={css.employeeWrapper}
									>
										<Avatar
											className={`${css.avatar} ${css.gridCell}`}
											id={employee.get("id")}
											size={20}
										/>
										<div className={`${css.employeeName} ${css.gridCell} text_truncate`}>
											{employee.get("name")}
										</div>
									</div>
								))
							)}

							<div data-testid="authorPicker" className={css.editPersonContainer}>
								{isEditing && authorPickerValue !== null ? (
									<EmployeePicker
										prependOptions={prependOptions}
										value={authorPickerValue}
										onChange={handleAuthorsChange}
									/>
								) : null}

								{isEditing && authorPickerValue === null ? (
									<div
										data-testid="addAuthorButton"
										onClick={() => handleAuthorsChange("")}
										className={`${css.addPersonButton} ${css.addAuthor}`}
									>
										Add Author
									</div>
								) : null}
							</div>
						</div>
					</div>

					<div data-testid="signersContainer" className={`${css.attributeContainer} ${css.multipleValues}`}>
						<div className={`${css.attributeLabel} ${css.gridCell} ${isEditing ? css.editView : ""}`}>
							Who signs off on this form:
						</div>
						<div className={`${css.employeesList} ${css.gridCell} ${isEditing ? css.isEditing : ""}`}>
							{isEditing ? (
								<DragAndDrop
									itemIds={displaySigners.map((signer, ii) => `${signer.get("id")}-${ii}`).toJS()}
									draggableItems={displaySigners.toJS().map((signer, ii) => ({
										...signer,
										drag_id: `${signer.id}-${ii}`,
									}))}
									keyValue="drag_id"
									handleOnDragEnd={onDragEndSigners}
									formatDraggableItems={(signer, index) =>
										formatDraggableItems(signer, removeSigner, "draggableSignerItem", index)
									}
								/>
							) : (
								<div>
									{displaySigners.size > 0
										? displaySigners.map((employee, ii) => (
												<div
													data-testid="signerItem"
													key={`${employee.get("id")}-${ii}`}
													className={css.employeeWrapper}
												>
													<Avatar
														className={`${css.avatar} ${css.gridCell}`}
														id={employee.get("id")}
														size={20}
													/>
													<div
														className={`${css.employeeName} ${css.gridCell} text_truncate`}
													>
														{employee.get("name")}
													</div>
												</div>
											))
										: "No signers have been assigned"}
								</div>
							)}

							<div data-testid="signerPicker" className={css.editPersonContainer}>
								{isEditing && signerPickerValue !== null ? (
									<EmployeePicker
										prependOptions={prependOptions.filterNot((item) =>
											["direct-reports"].includes(item.get("id"))
										)}
										value={signerPickerValue}
										onChange={handleSignersChange}
									/>
								) : null}

								{isEditing && signerPickerValue === null ? (
									<div
										data-testid="addSignerButton"
										onClick={() => handleSignersChange("")}
										className={`${css.addPersonButton} ${css.addAuthor}`}
									>
										Add Signer
									</div>
								) : null}
							</div>
						</div>
					</div>

					{displayAuthors.size > 0 ? (
						<div className={css.attributeContainer}>
							<div className={`${css.attributeLabel} ${css.gridCell}`}>Authors due date:</div>
							{dateType === "static" ? (
								<div className={`${css.attributeValue} ${css.gridCell}`} data-testid="authorDueDate">
									{isEditing ? (
										<DatePicker
											dateEngine="moment"
											hasTime={true}
											defaultTime={17}
											onChange={setAuthorsDueDate}
											value={localAuthorsDueDate}
										/>
									) : (
										authorsDueDate.format("MMM D YYYY h:mm a")
									)}
								</div>
							) : (
								<div
									style={{
										display: "flex",
										alignItems: "center",
									}}
								>
									{isEditing ? (
										<div style={{ width: "45px" }}>
											<TextInput
												type="number"
												value={!moment.isMoment(localAuthorsDueDate) ? localAuthorsDueDate : 0}
												onChange={(value) => {
													if (value >= 0) {
														setAuthorsDueDate(value);
													}
												}}
											/>
										</div>
									) : (
										localAuthorsDueDate
									)}
									<div style={{ marginLeft: "5px" }}>{`Day${
										localAuthorsDueDate === 1 ? "" : "s"
									} from start date`}</div>
								</div>
							)}
						</div>
					) : null}

					{displaySigners.size > 0 ? (
						<div className={css.attributeContainer} data-testid="signersDueDateContainer">
							<div className={`${css.attributeLabel} ${css.gridCell}`}>Signers due date:</div>
							{dateType === "static" ? (
								<div className={`${css.attributeValue} ${css.gridCell}`} data-testid="signerDueDate">
									{isEditing ? (
										<DatePicker
											dateEngine="moment"
											hasTime={true}
											defaultTime={17}
											onChange={setSignersDueDate}
											value={localSignersDueDate}
										/>
									) : (
										signersDueDate.format("MMM D YYYY h:mm a")
									)}
								</div>
							) : (
								<div
									style={{
										display: "flex",
										alignItems: "center",
									}}
								>
									{isEditing ? (
										<div style={{ width: "45px" }} data-testid="signerDueDateInput">
											<TextInput
												type="number"
												value={!moment.isMoment(localSignersDueDate) ? localSignersDueDate : 0}
												onChange={(value) => {
													if (value >= 0) {
														setSignersDueDate(value);
													}
												}}
											/>
										</div>
									) : (
										localSignersDueDate
									)}
									<div style={{ marginLeft: "5px" }}>{`Day${
										localSignersDueDate === 1 ? "" : "s"
									} from author due date`}</div>
								</div>
							)}
						</div>
					) : null}

					<div data-testid="visibilityContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
						<div className={`${css.attributeLabel} ${css.gridCell}`}>
							Visible to the subject (when all authors/signers are finished):
						</div>
						{isEditing ? (
							<div className={`${css.toggleWrapper} ${css.gridCell}`}>
								<Toggle onChange={setVisibleToSubject} value={localVisibleToSubject} width={40} />
							</div>
						) : (
							<div
								data-testid="visibilityValue"
								className={`${css.attributeValue} ${css.gridCell} ${
									visibleToSubject ? css.isGreen : css.isRed
								}`}
							>
								{visibleToSubject ? (
									<FontAwesomeIcon data-testid="fas-check" icon={faCheck} />
								) : (
									<FontAwesomeIcon data-testid="fas-times" icon={faTimes} />
								)}
							</div>
						)}
					</div>
					{redactedSection ? (
						<div data-testid="redactedContainer" className={`${css.attributeContainer} ${css.nonGrid}`}>
							<div className={css.attributeLabel}>Redact authors:</div>
							{redactedSection}
						</div>
					) : null}
				</div>
			</div>

			<div data-testid="cardActions" className={css.cardActions}>
				{isEditing ? (
					<div>
						<div
							data-testid="saveButton"
							className={`${css.action} ${css.blue} ${hasError ? css.isDisabled : ""}`}
							onClick={hasError ? () => {} : handleSave}
						>
							{isNewForm ? "Add" : "Update"}
						</div>
						<div data-testid="cancelButton" className={`${css.action} ${css.white}`} onClick={handleCancel}>
							Cancel
						</div>
					</div>
				) : (
					<div>
						<div data-testid="editButton" className={`${css.action} ${css.blue}`} onClick={props.onEdit}>
							Edit
						</div>
						<div data-testid="deleteButton" className={`${css.action} ${css.red}`} onClick={props.onDelete}>
							Delete
						</div>
					</div>
				)}

				<div className={css.cardErrors}>
					{validDueDates ? null : (
						<div className={css.cardError}>
							The author due date must be the same or come before the signer due date.
						</div>
					)}
					{isAboveLowerDateBoundary ? null : (
						<div data-testid="invalidMinimumDueDateMessage" className={css.cardError}>
							{invalidMinimumDueDateMessage}
						</div>
					)}
					{formSelected ? null : <div className={css.cardError}>A form must be selected.</div>}
					{authorRequiredAndSelected ? null : (
						<div className={css.cardError}>At least one author must be selected </div>
					)}
				</div>
			</div>
			<BeforeUnload checkForUnsavedData={checkFormChange} />
		</div>
	);
};

BasicReviewFormCard.propTypes = {
	/** Handle clicking the edit action */
	onEdit: PropTypes.func,
	/** Handle clicking the delete action */
	onDelete: PropTypes.func,
	/** Handle clicking the cancel action */
	onCancel: PropTypes.func,
	/** Handle clicking the save action */
	onSave: PropTypes.func,
	/** Will put the card in edit mode if true */
	isEditing: PropTypes.bool,
	/** Will put a 'form has been deleted' badge on the card */
	isDeleted: PropTypes.bool,
	/** Additional class */
	className: PropTypes.string,
	/** Will display a green check or red 'x' */
	visibleToSubject: PropTypes.bool.isRequired,
	/** Will display a green check or red 'x' */
	redacted: PropTypes.bool.isRequired,
	/** Name of the form */
	name: PropTypes.string.isRequired,
	/** days to complete the form for authors */
	authorsDueDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.number, PropTypes.string]).isRequired,
	/** days to complete the form for signers */
	signersDueDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.number, PropTypes.string]).isRequired,
	/** An array of author objects with an 'id' and 'name' */
	authors: PropTypes.instanceOf(List).isRequired,
	/** An array of signer objects with an 'id' and 'name' */
	signers: PropTypes.instanceOf(List).isRequired,
	/** A map of field names and true or false.
	 * a value of true means that the field cannot
	 * be edited by the current user. */
	lockedFields: PropTypes.instanceOf(List),
	/** the minimum due date allowed */
	minimumDueDate: PropTypes.instanceOf(moment),
	/** the message to be displayed if the min due date has been broken */
	invalidMinimumDueDateMessage: PropTypes.string,
	/** the selected form for the dropdown. If this value is `undefined`, then
	 * the dropdown is not displayed. This can also be null if nothing is
	 * selected. */
	editableForm: PropTypes.instanceOf(Map),
	dateType: PropTypes.string,
	authorRequired: PropTypes.bool,
	isNewForm: PropTypes.bool.isRequired,
};

export default BasicReviewFormCard;
